import {
  collection,
  query,
  where,
  orderBy,
  limit,
  getCountFromServer,
} from "firebase/firestore";
import { useState, useEffect, useMemo, useCallback } from "react";
import { useCollectionData } from "react-firebase-hooks/firestore";
import { useAuthContext } from "@/context/AuthContext";
import { PointsActionCompleted } from "@t3/shared";
import { firestore } from "@/firebase";
import { pointsActionConverter } from "@/converters";
import {
  Box,
  Stack,
  Typography,
  CircularProgress,
  Container,
  Button,
  Divider,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { useCompanyConfigContext } from "@/context/CompanyConfigContext";
import { TransactionCard } from "./components/TransactionCard";
import { LoadMoreButton, NoMoreItemsIndicator } from "@/components/common";
import { useSearchParams, useNavigate } from "react-router-dom";
import { PointSourceBadge } from "@/components/PrpNavigation/PointSourcesBadges";

const deduplicateByCartId = (
  actions: PointsActionCompleted[],
): PointsActionCompleted[] => {
  const cartIdMap = new Map<string, PointsActionCompleted>();

  for (const action of actions) {
    const cartId =
      action.type === "purchase" || action.type === "refund"
        ? action.typeMeta.cartId
        : null;

    if (cartId) {
      cartIdMap.set(cartId, action);
    } else {
      cartIdMap.set(`no-cart-${action.id}`, action);
    }
  }

  return Array.from(cartIdMap.values());
};

export const ShopHistory = () => {
  const ITEMS_PER_PAGE = 100;
  const { user } = useAuthContext();
  const { company } = useCompanyConfigContext();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [myLimit, setMyLimit] = useState(ITEMS_PER_PAGE);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [pointsActions, setPointsActions] = useState<PointsActionCompleted[]>(
    [],
  );

  const pointSourceFilter = searchParams.get("pointSource");

  const clearFilter = () => {
    navigate("/shop/history");
  };

  const pointsActionsBaseQuery = useMemo(() => {
    const pointsActionsRef = collection(
      firestore,
      `company/${company.id}/points-actions`,
    ).withConverter(pointsActionConverter);

    if (pointSourceFilter) {
      return query(
        pointsActionsRef,
        where("typeMeta.userId", "==", user.id),
        where("status", "==", "completed"),
        where("typeMeta.pointSource", "==", pointSourceFilter),
        orderBy("modifiedAt", "desc"),
      );
    }

    return query(
      pointsActionsRef,
      where("typeMeta.userId", "==", user.id),
      where("status", "==", "completed"),
      orderBy("modifiedAt", "desc"),
    );
  }, [pointSourceFilter]);

  const pointsActionsQuery = useMemo(() => {
    if (!pointsActionsBaseQuery) return null;
    return query(pointsActionsBaseQuery, limit(myLimit));
  }, [myLimit, pointsActionsBaseQuery]);

  const [pointsActionsRaw, loading, error] =
    useCollectionData(pointsActionsQuery);

  useEffect(() => {
    if (!pointsActionsBaseQuery) {
      setTotalCount(0);
      return;
    }

    getCountFromServer(pointsActionsBaseQuery)
      .then((snapshot) => {
        setTotalCount(snapshot.data().count);
      })
      .catch((error) => {
        console.error("Error fetching action count:", error);
      });
  }, [pointsActionsBaseQuery]);

  const loadMore = useCallback(() => {
    setMyLimit((prev) => prev + ITEMS_PER_PAGE);
  }, []);

  const hasLoadedAll = (pointsActionsRaw?.length || 0) >= totalCount;
  const hasMore = !hasLoadedAll;

  useEffect(() => {
    if (!loading && pointsActionsRaw) {
      const deduplicatedActions = deduplicateByCartId(
        pointsActionsRaw as PointsActionCompleted[],
      );
      setPointsActions(deduplicatedActions);
    }
  }, [pointsActionsRaw, loading]);

  if (error) {
    console.error("Error fetching points actions:", error);
  }

  if (loading && pointsActions.length === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  // Get point source configuration and user point source amount
  const pointSourceConfig =
    pointSourceFilter && company.pointSources?.[pointSourceFilter]
      ? company.pointSources[pointSourceFilter]
      : null;

  const userPointSourceAmount =
    pointSourceFilter && user.pointSources?.[pointSourceFilter]
      ? user.pointSources[pointSourceFilter]
      : 0;

  return (
    <Container maxWidth="md" sx={{ p: 0 }}>
      <Stack spacing={2}>
        {/* Filter Header */}
        {pointSourceFilter && pointSourceConfig && company.pointSources && (
          <>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
                py: 2,
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 600,
                  color: "text.primary",
                }}
              >
                Rewards From:
              </Typography>
              <PointSourceBadge
                sourceKey={pointSourceFilter}
                amount={userPointSourceAmount}
                companyPointSources={company.pointSources}
                index={0}
                totalSources={1}
              />
              <Button
                startIcon={<Close />}
                onClick={clearFilter}
                sx={{
                  textTransform: "none",
                  color: "text.secondary",
                  fontSize: "1rem",
                }}
              >
                Clear Filter
              </Button>
            </Box>
            <Divider />
          </>
        )}

        {pointsActions.map((action) => (
          <TransactionCard key={action.id} action={action} company={company} />
        ))}

        {hasMore ? (
          <LoadMoreButton loadMoreMessages={loadMore} loading={loading} />
        ) : (
          pointsActions.length > 0 && (
            <NoMoreItemsIndicator
              message="You've reached the end"
              subMessage="No more transactions to load"
            />
          )
        )}

        {pointsActions.length === 0 && !loading && (
          <Box textAlign="center" py={8}>
            <Typography variant="h6" color="text.secondary">
              No transaction history yet
            </Typography>
          </Box>
        )}
      </Stack>
    </Container>
  );
};
